import { useState, useEffect } from 'react';
import { get } from '../services/apiClient';

/**
 * Custom hook to fetch and manage material types
 */
export const useMaterialTypes = () => {
  const [materialTypes, setMaterialTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMaterialTypes = async () => {
      try {
        setLoading(true);
        const response = await get('/api/materials/types');
        
        if (response.success) {
          setMaterialTypes(response.materialTypes);
          setError(null);
        } else {
          throw new Error('Failed to fetch material types');
        }
      } catch (err) {
        console.error('Error fetching material types:', err);
        setError(err.message || 'Failed to load material types');
        
        // Fallback to default types if API fails
        setMaterialTypes({
          study_notes: {
            label: 'Study Notes',
            description: 'Personal notes and study materials created by students',
            icon: 'notes'
          },
          study_guide: {
            label: 'Study Guide',
            description: 'Student-created guides for exam preparation',
            icon: 'guide'
          },
          practice_materials: {
            label: 'Practice Materials',
            description: 'Student-generated practice problems and exercises',
            icon: 'practice'
          },
          course_resources: {
            label: 'Course Resources',
            description: 'Educational resources and supplementary materials',
            icon: 'resources'
          },
          group_study: {
            label: 'Group Study',
            description: 'Collaborative study materials created by student groups',
            icon: 'group'
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMaterialTypes();
  }, []);

  return { materialTypes, loading, error };
};

/**
 * Get material type icon component
 */
export const getMaterialTypeIcon = (type) => {
  const icons = {
    study_notes: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
        <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h6a1 1 0 001-1V3a2 2 0 012 2v6.5a.5.5 0 01-.5.5h-11A.5.5 0 014 11.5V5zm3 2.5a.5.5 0 01.5-.5h5a.5.5 0 010 1h-5a.5.5 0 01-.5-.5zm0 2a.5.5 0 01.5-.5h5a.5.5 0 010 1h-5a.5.5 0 01-.5-.5z" clipRule="evenodd" />
      </svg>
    ),
    study_guide: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
      </svg>
    ),
    practice_materials: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm2 10a1 1 0 10-2 0v3a1 1 0 102 0v-3zm2-3a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1zm4-1a1 1 0 10-2 0v7a1 1 0 102 0V8z" clipRule="evenodd" />
      </svg>
    ),
    course_resources: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
      </svg>
    ),
    group_study: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
      </svg>
    )
  };
  
  return icons[type] || icons.study_notes;
};